import axios from "axios";
import { AppThunk } from "store/store";
import { API_ENDPOINTS } from "config/apiConfig";
import { axiosInstance } from "config/axiosConfig";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "./loadingSlice";
import { Delete, CompanyFilter, CompanyListPaginate } from "common/interfaces";
import download from "js-file-download";
import React from "react";
import { PAGINATION } from "common/enums/Pagination.enum";

interface CompanyState {
  companyData: CompanyListPaginate | null;
  companyDeleteData: Delete | null;
  companyCsvData: any;
  error: string | null;
}

const initialState: CompanyState = {
  companyData: null,
  companyDeleteData: null,
  companyCsvData: null,
  error: null,
};

export const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    companySuccess: (state, action: PayloadAction<any>) => {
      state.companyData = action.payload;
      state.error = null;
    },
    companyFail: (state, action: PayloadAction<string>) => {
      state.companyData = null;
      state.error = action.payload;
    },
    companyDeleteSuccess: (state, action: PayloadAction<any>) => {
      state.companyDeleteData = action.payload;
      state.error = null;
    },
    companyDeleteFail: (state, action: PayloadAction<string>) => {
      state.companyDeleteData = null;
      state.error = action.payload;
    },
    companyCsvSuccess: (state, action: PayloadAction<any>) => {
      state.companyCsvData = action.payload;
      state.error = null;
    },
    companyCsvFail: (state, action: PayloadAction<string>) => {
      state.companyCsvData = null;
      state.error = action.payload;
    },
    companyCleanData: (state) => {
      state.companyData = null;
      state.companyDeleteData = null;
      state.error = null;
    },
  },
});

export const {
  companySuccess,
  companyFail,
  companyDeleteSuccess,
  companyDeleteFail,
  companyCsvSuccess,
  companyCsvFail,
  companyCleanData,
} = companySlice.actions;

export const fetchCompany =
  (
    companyFilter: CompanyFilter | undefined,
    currentPage: number | undefined,
  ): AppThunk =>
  async (dispatch) => {
    const controller = new AbortController();
    dispatch(showLoading());
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.COMPANY_INFO, {
        params: {
          page: currentPage || PAGINATION.PAGE,
          limit: PAGINATION.LIMIT,
          ...companyFilter,
        },
        signal: controller.signal,
      });
      dispatch(companySuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(companyFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const deleteCompany =
  (codes: React.Key[]): AppThunk =>
  async (dispatch) => {
    const controller = new AbortController();
    dispatch(showLoading());
    try {
      const response = await axiosInstance.delete(
        API_ENDPOINTS.COMPANY_INFO_DELETE,
        { data: { codes }, signal: controller.signal },
      );
      dispatch(companyDeleteSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(companyDeleteFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export const downloadCompanyCsv =
  (fileName: string): AppThunk =>
  async (dispatch) => {
    const controller = new AbortController();
    dispatch(showLoading());
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.COMPANY_INFO_CSV, {
        signal: controller.signal,
        responseType: "blob",
      });
      download(response.data, fileName, "text/csv");
      dispatch(companyCsvSuccess(response.data));
    } catch (error: any) {
      if (axios.isCancel(error)) {
        console.log("Request was canceled", error.message);
      } else {
        dispatch(companyCsvFail(error.message || "Something went wrong"));
      }
    } finally {
      dispatch(hideLoading());
    }
  };

export default companySlice.reducer;
